import _fp from "lodash/fp"
import { FormRootState, FormStore } from "../../redux/formStore"
import { getDisplayIdentity } from '@fusion-advantage/formlib'
import {
	AppFormRelayForwardUpdatedRenderStateActionType,
	AppFormRelayInterface,
	AppFormRelayResult,
	BackcompatRenderState,
	dangerousGetStoreResult,
	finalizeForSubmitArgs,
	finalizeForSubmitResult,
	generateDbImportResult,
	getLastChosenAtResult,
	loadExportArgs,
	loadExportResult,
	loadExportV2Args,
	loadExportV2Result,
	RenderMedia,
	RenderPlatform,
	RenderSliceSettings,
	SettingId,
} from '@fusion-advantage/backcompat'
import { ActionReducerMapBuilder, createAction, createSlice } from "@reduxjs/toolkit"
import { FusionThemeOverridable } from "../../theme/FusionStyle"
import { loadExport } from "../persistence/loadExportThunk"
import { formDestroy } from "../../redux/formActions"
import selectDbImport from "../../redux/selectDbImport"
import { formFinalizeForSubmit } from "../persistence/submitForm"
import { selectLastChosenAt } from "../../redux/selectLastChosenAt"

export type RenderState = BackcompatRenderState & {
	theme: Partial<FusionThemeOverridable>
}

// don't use a `true` boolean default in this object;
// flip the phrasing of the name so a false default makes sense
export const DefaultRenderSliceSettings: Readonly<RenderSliceSettings> = {
	[SettingId.ShowDebugInfo]: false,
}

export const initialRenderState: RenderState = {
	t1NavAvailableFor: {},
	media: RenderMedia.Screen,
	...DefaultRenderSliceSettings,
	theme: {},
	t1NavOpen: false,
	loadedWorkItem: null,
	formLoading: false,
	form: null,
	platform: RenderPlatform.Web,
	authedIdentity: null,
	impersonatingIdentity: null,
	locationLoading: false,
	importLocation: null,
	importLoading: false,
	previewMode: false,
}

export const appFormRelayForwardUpdatedRenderStateAction = createAction<RenderState>(AppFormRelayForwardUpdatedRenderStateActionType)

// renderSlice is built into the reducer on redux stores in both formrender
// and the consuming frontend.  the frontend will dispatch updateRenderStore
// actions, which update its copy of the renderSlice, and then those updates
// are automatically dispatched over to formrender by subscribeAppFormRelay.
export const createRenderSlice = (extraReducers: (builder: ActionReducerMapBuilder<RenderState>) => void) => {
	return createSlice({
		name: 'render',
		initialState: initialRenderState,
		reducers: {},
		extraReducers,
	})
}

export const selectFormDisplayIdentity = (state: FormRootState) => {
	return getDisplayIdentity(state.render)
}

export class AppFormRelayImpl implements AppFormRelayInterface {
	formStore: FormStore|null
	constructor(formStore: FormStore) {
		this.formStore = formStore
	}
	cleanup() {
		this.formStore?.dispatch(formDestroy())
		this.formStore = null
	}
	// WARNING
	// this is only here in case we need to create an emergency patch for a
	// baked formrender version; do not use for normal functionality !!
	dangerousGetStore(): dangerousGetStoreResult {
		return {
			formStore: this.formStore
		}
	}
	// this is used interally to forward the updated render slice state
	// from the write version of the slice in the frontend
	// to the read-only replica of the slice in the renderer
	forwardUpdatedRenderState(update: RenderState) {
		if(this.formStore) {
			this.formStore.dispatch(appFormRelayForwardUpdatedRenderStateAction(update))
		}
	}
	/**
	 * @deprecated: 20260120
	 * replaced by v2
	 * warning: legacy renderers can throw errors,
	 * so please try/catch in receiving frontend code
	 */
	loadExport(args: loadExportArgs): AppFormRelayResult<loadExportResult> {
		throw new Error('not implemented')
	}
	async loadExportV2(args: loadExportV2Args): Promise<AppFormRelayResult<loadExportV2Result>> {
		if(!this.formStore) {
			return { detached: true }
		}
		const success = await this.formStore.dispatch(loadExport(args.draftExportSupplement))
		return {
			detached: false,
			result: {
				success,
			}
		}
	}
	// 20260414 allow frontend to check if form has been updated by the user
	getLastChosenAt(): AppFormRelayResult<getLastChosenAtResult> {
		if(!this.formStore) {
			return { detached: true }
		}
		return {
			detached: false,
			result: {
				lastChosenAt: selectLastChosenAt(this.formStore.getState()),
			}
		}
	}
	generateImport(): AppFormRelayResult<generateDbImportResult> {
		if(!this.formStore) {
			return { detached: true }
		}
		return {
			detached: false,
			result: {
				dbImport: selectDbImport(this.formStore.getState())
			}
		}
	}
	/**
	 * @deprecated: 20251120
	 * as of full offline, no longer used
	 */
	generatePseudoOfflineDraft(): AppFormRelayResult<never> {
		throw new Error('not implemented')
	}
	finalizeForSubmit({submitToCode}: finalizeForSubmitArgs): AppFormRelayResult<finalizeForSubmitResult> {
		if(!this.formStore) {
			return { detached: true }
		}
		const prevState = this.formStore.getState()
		this.formStore.dispatch(formFinalizeForSubmit({submitToCode}))
		const nextState = this.formStore.getState()
		return {
			detached: false,
			result: {
				updated: prevState !== nextState,
			}
		}
	}
}
